import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el as elM, en as enM } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Issue {
  id: string;
  title: string;
  description: string;
  category: 'Technical' | 'Resource' | 'Scope' | 'Budget' | 'Schedule' | 'External';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
  owner: string;
  date_identified: string;
  deadline: string;
  impact: string[];
  action_plan: string;
  risk_ref: string;
}

const priorityConfig = {
  Critical: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' },
  High: { bg: '#fff7ed', text: '#c2410c', border: '#f97316' },
  Medium: { bg: '#fef9c3', text: '#854d0e', border: '#eab308' },
  Low: { bg: '#f0fdf4', text: '#166534', border: '#22c55e' },
};

const statusConfig = {
  Open: { bg: '#fee2e2', text: '#991b1b' },
  'In Progress': { bg: '#dbeafe', text: '#1e40af' },
  Resolved: { bg: '#dcfce7', text: '#166534' },
  Closed: { bg: '#f3f4f6', text: '#6b7280' },
};

const categoryColors: Record<string, string> = {
  Technical: '#4f46e5', Resource: '#0ea5e9', Scope: '#8b5cf6',
  Budget: '#10b981', Schedule: '#f59e0b', External: '#ef4444',
};

const impactOptions = ['Schedule', 'Budget', 'Scope', 'Quality', 'Resources'];

const Issues: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit, openCreate }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? enM : elM;
  const L = M.issues;
  const C = M.common;
  const [activeTab, setActiveTab] = useState<'dashboard' | 'log'>('dashboard');
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIssue, setEditingIssue] = useState<Issue | null>(null);
  const [saving, setSaving] = useState(false);
  const [aiPlanLoading, setAiPlanLoading] = useState(false);
  const [form, setForm] = useState<Partial<Issue>>({
    title: '', description: '', category: 'Technical',
    priority: 'Medium', status: 'Open', owner: '',
    date_identified: new Date().toISOString().split('T')[0],
    deadline: '', impact: [], action_plan: '', risk_ref: '',
  });

  const [stakeholderNames, setStakeholderNames] = useState<string[]>([]);

  useEffect(() => { fetchData(); fetchStakeholderNames(); }, []);
  useEffect(() => { if (openCreate && canEdit) { setActiveTab('log'); setShowModal(true); } }, [openCreate]);

  const fetchStakeholderNames = async () => {
    try {
      const res = await axios.get(`${API}/modules/${project.id}/Stakeholders`, { headers: { Authorization: `Bearer ${token}` } });
      const list: { name: string }[] = res.data?.data?.stakeholders || [];
      setStakeholderNames(list.map(s => s.name).filter(Boolean));
    } catch { }
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${API}/modules/${project.id}/Issues`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.data?.issues) setIssues(response.data.data.issues);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveIssues = async (updated: Issue[]) => {
    await axios.put(
      `${API}/modules/${project.id}/Issues`,
      { data: { issues: updated }, userId: user.id },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  const handleSave = async () => {
    if (!form.title) return;
    setSaving(true);
    try {
      const newIssue: Issue = {
        id: editingIssue?.id || `I${Date.now()}`,
        title: form.title || '', description: form.description || '',
        category: form.category || 'Technical', priority: form.priority || 'Medium',
        status: form.status || 'Open', owner: form.owner || '',
        date_identified: form.date_identified || new Date().toISOString().split('T')[0],
        deadline: form.deadline || '', impact: form.impact || [],
        action_plan: form.action_plan || '', risk_ref: form.risk_ref || '',
      };
      const updated = editingIssue
        ? issues.map(i => i.id === editingIssue.id ? newIssue : i)
        : [...issues, newIssue];
      await saveIssues(updated);
      setIssues(updated);
      resetForm();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    const updated = issues.filter(i => i.id !== id);
    await saveIssues(updated);
    setIssues(updated);
  };

  const handleStatusChange = async (id: string, status: Issue['status']) => {
    const updated = issues.map(i => i.id === id ? { ...i, status } : i);
    await saveIssues(updated);
    setIssues(updated);
  };

  const resetForm = () => {
    setForm({ title: '', description: '', category: 'Technical', priority: 'Medium', status: 'Open', owner: '', date_identified: new Date().toISOString().split('T')[0], deadline: '', impact: [], action_plan: '', risk_ref: '' });
    setEditingIssue(null);
    setShowModal(false);
  };

  const toggleImpact = (imp: string) => {
    const current = form.impact || [];
    setForm({ ...form, impact: current.includes(imp) ? current.filter(i => i !== imp) : [...current, imp] });
  };

  const exportToExcel = () => {
    const [h0, h1, h2, h3, h4, h5, h6, h7, h8, h9] = L.excelHeaders;
    const data = issues.map((issue, i) => ({
      '#': i + 1, [h0]: issue.title, [h1]: issue.description,
      [h2]: issue.category, [h3]: issue.priority,
      [h4]: issue.status, [h5]: issue.owner,
      [h6]: issue.date_identified, [h7]: issue.deadline,
      [h8]: issue.impact.join(', '), [h9]: issue.action_plan,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Issues');
    XLSX.writeFile(wb, `Issues_${project.name}.xlsx`);
  };

  const openIssues = issues.filter(i => i.status === 'Open').length;
  const inProgressIssues = issues.filter(i => i.status === 'In Progress').length;
  const resolvedIssues = issues.filter(i => i.status === 'Resolved' || i.status === 'Closed').length;
  const normPriority = (p: string) => p?.charAt(0).toUpperCase() + p?.slice(1).toLowerCase();
  const criticalIssues = issues.filter(i => normPriority(i.priority) === 'Critical' && i.status !== 'Closed' && i.status !== 'Resolved');
  const highIssues = issues.filter(i => normPriority(i.priority) === 'High' && i.status !== 'Closed' && i.status !== 'Resolved');

  const categoryStats = ['Technical', 'Resource', 'Scope', 'Budget', 'Schedule', 'External'].map(cat => ({
    name: cat, count: issues.filter(i => i.category === cat).length, color: categoryColors[cat],
  })).filter(c => c.count > 0);

  const maxCategoryCount = Math.max(...categoryStats.map(c => c.count), 1);

  return (
    <div style={styles.container}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={onBack}>{C.myProjects}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbLink} onClick={onBack}>{project.name}</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbCurrent}>Issues</span>
      </div>

      <div style={styles.main}>
        <div style={styles.titleRow}>
          <h2 style={styles.pageTitle}>Issues</h2>
          <div style={styles.titleButtons}>
            <button style={styles.exportBtn} onClick={exportToExcel}>⬇ Excel</button>
            {canEdit && (
              <button style={styles.addBtn} onClick={() => setShowModal(true)}>{L.btnAddIssue}</button>
            )}
          </div>
        </div>

        <div style={styles.tabWrapper}>
          <div style={styles.tabContainer}>
            <button style={{ ...styles.tab, ...(activeTab === 'dashboard' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('dashboard')}>Dashboard</button>
            <button style={{ ...styles.tab, ...(activeTab === 'log' ? styles.tabActive : styles.tabInactive) }} onClick={() => setActiveTab('log')}>Issue Log</button>
          </div>
        </div>

        {loading ? <p style={styles.loading}>{C.loading}</p> : (
          <>
            {activeTab === 'dashboard' && (
              <div>
                <div style={styles.summaryRow}>
                  <div style={styles.summaryCard}><div style={{ ...styles.summaryValue, color: '#ef4444' }}>{openIssues}</div><div style={styles.summaryLabel}>{L.cardOpen}</div></div>
                  <div style={styles.summaryCard}><div style={{ ...styles.summaryValue, color: '#0ea5e9' }}>{inProgressIssues}</div><div style={styles.summaryLabel}>{L.cardInProgress}</div></div>
                  <div style={styles.summaryCard}><div style={{ ...styles.summaryValue, color: '#10b981' }}>{resolvedIssues}</div><div style={styles.summaryLabel}>{L.cardResolved}</div></div>
                  <div style={styles.summaryCard}><div style={styles.summaryValue}>{issues.length}</div><div style={styles.summaryLabel}>{L.cardTotal}</div></div>
                </div>

                <div style={styles.dashboardGrid}>
                  <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>{L.sectionHighPriority}</h3>
                    {criticalIssues.length === 0 && highIssues.length === 0 ? (
                      <div style={styles.noIssues}><span style={{ fontSize: '32px' }}>✅</span><p>{L.emptyHighPriority}</p></div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[...criticalIssues, ...highIssues].map(issue => {
                          const pc = priorityConfig[normPriority(issue.priority)] || { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' };
                          const sc = statusConfig[issue.status] || { bg: '#f3f4f6', text: '#374151' };
                          return (
                            <div key={issue.id} style={{ ...styles.priorityCard, borderLeft: `4px solid ${pc.border}` }}>
                              <div style={styles.priorityCardHeader}>
                                <span style={{ ...styles.priorityBadge, backgroundColor: pc.bg, color: pc.text }}>{issue.priority}</span>
                                <span style={{ ...styles.statusBadge, backgroundColor: sc.bg, color: sc.text }}>{issue.status}</span>
                              </div>
                              <div style={styles.priorityCardTitle}>{issue.title}</div>
                              <div style={styles.priorityCardMeta}>
                                {issue.owner && <span>👤 {issue.owner}</span>}
                                {issue.deadline && <span>📅 {issue.deadline}</span>}
                                <span style={{ ...styles.categoryChip, backgroundColor: categoryColors[issue.category] + '20', color: categoryColors[issue.category] }}>{issue.category}</span>
                              </div>
                              {issue.action_plan && <div style={styles.actionPlanPreview}><strong>Action Plan:</strong> {issue.action_plan}</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div style={styles.section}>
                    <h3 style={styles.sectionTitle}>{L.sectionByCategory}</h3>
                    {categoryStats.length === 0 ? (
                      <div style={styles.noIssues}><p>{L.emptyIssues}</p></div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {categoryStats.sort((a, b) => b.count - a.count).map(cat => (
                          <div key={cat.name} style={styles.categoryRow}>
                            <div style={styles.categoryLabel}>
                              <span style={{ ...styles.categoryDot, backgroundColor: cat.color }} />
                              <span style={styles.categoryName}>{cat.name}</span>
                            </div>
                            <div style={styles.categoryBarBg}>
                              <div style={{ ...styles.categoryBar, width: `${(cat.count / maxCategoryCount) * 100}%`, backgroundColor: cat.color }} />
                            </div>
                            <span style={styles.categoryCount}>{cat.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <h3 style={{ ...styles.sectionTitle, marginTop: '24px' }}>{L.sectionByStatus}</h3>
                    <div style={styles.statusGrid}>
                      {Object.entries(statusConfig).map(([status, config]) => {
                        const count = issues.filter(i => i.status === status).length;
                        return (
                          <div key={status} style={{ ...styles.statusCard, backgroundColor: config.bg }}>
                            <div style={{ ...styles.statusCount, color: config.text }}>{count}</div>
                            <div style={{ ...styles.statusLabel, color: config.text }}>{status}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'log' && (
              <div style={styles.section}>
                {issues.length === 0 ? (
                  <div style={styles.emptyState}>
                    <span style={{ fontSize: '48px' }}>🔧</span>
                    <p>{L.emptyIssues}</p>
                    <p style={styles.emptySubtext}>{L.emptyIssuesSubtext}</p>
                    {canEdit && <button style={styles.addBtn} onClick={() => setShowModal(true)}>{L.btnAddIssue}</button>}
                  </div>
                ) : (
                  <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                      <thead>
                        <tr style={styles.tableHeader}>
                          <th style={styles.th}>#</th>
                          {L.tableHeaders.slice(0, 7).map(h => <th key={h} style={styles.th}>{h}</th>)}
                          {canEdit && <th style={styles.th}>{L.tableHeaders[7]}</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {issues.map((issue, i) => {
                          const pc = priorityConfig[normPriority(issue.priority)] || { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' };
                          const sc = statusConfig[issue.status] || { bg: '#f3f4f6', text: '#374151' };
                          return (
                            <tr key={issue.id} style={styles.tableRow}>
                              <td style={styles.td}>{i + 1}</td>
                              <td style={styles.td}>
                                <div style={styles.issueTitle}>{issue.title}</div>
                                {issue.description && <div style={styles.issueDesc}>{issue.description}</div>}
                              </td>
                              <td style={styles.td}>
                                <span style={{ ...styles.categoryChip, backgroundColor: categoryColors[issue.category] + '20', color: categoryColors[issue.category] }}>{issue.category}</span>
                              </td>
                              <td style={styles.td}>
                                <span style={{ ...styles.priorityBadge, backgroundColor: pc.bg, color: pc.text }}>{issue.priority}</span>
                              </td>
                              <td style={styles.td}>
                                {canEdit ? (
                                  <select style={{ ...styles.statusSelect, backgroundColor: sc.bg, color: sc.text }} value={issue.status} onChange={e => handleStatusChange(issue.id, e.target.value as Issue['status'])}>
                                    <option value="Open">Open</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="Resolved">Resolved</option>
                                    <option value="Closed">Closed</option>
                                  </select>
                                ) : (
                                  <span style={{ ...styles.statusBadge, backgroundColor: sc.bg, color: sc.text }}>{issue.status}</span>
                                )}
                              </td>
                              <td style={styles.td}>{issue.owner || '—'}</td>
                              <td style={styles.td}>
                                {issue.deadline ? (
                                  <span style={{ fontSize: '12px', color: new Date(issue.deadline) < new Date() && issue.status !== 'Resolved' && issue.status !== 'Closed' ? '#ef4444' : '#374151' }}>{issue.deadline}</span>
                                ) : '—'}
                              </td>
                              <td style={styles.td}>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                                  {(issue.impact || []).map((imp, j) => <span key={j} style={styles.impactPill}>{imp}</span>)}
                                </div>
                              </td>
                              {canEdit && (
                                <td style={styles.td}>
                                  <div style={{ display: 'flex', gap: '6px' }}>
                                    <button style={styles.editBtn} onClick={() => { setEditingIssue(issue); setForm(issue); setShowModal(true); }}>✎</button>
                                    <button style={styles.deleteBtn} onClick={() => handleDelete(issue.id)}>✕</button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && canEdit && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{editingIssue ? L.modalEditTitle : L.modalNewTitle}</h2>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldTitle}</label>
              <input style={styles.input} value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} placeholder={L.fieldTitlePlaceholder} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldDescription}</label>
              <textarea style={{ ...styles.input, height: '70px', resize: 'none' }} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={L.fieldDescriptionPlaceholder} />
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldCategory}</label>
                <select style={styles.select} value={form.category || 'Technical'} onChange={e => setForm({ ...form, category: e.target.value as Issue['category'] })}>
                  <option value="Technical">Technical</option>
                  <option value="Resource">Resource</option>
                  <option value="Scope">Scope</option>
                  <option value="Budget">Budget</option>
                  <option value="Schedule">Schedule</option>
                  <option value="External">External</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldPriority}</label>
                <select style={styles.select} value={form.priority || 'Medium'} onChange={e => setForm({ ...form, priority: e.target.value as Issue['priority'] })}>
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldStatus}</label>
                <select style={styles.select} value={form.status || 'Open'} onChange={e => setForm({ ...form, status: e.target.value as Issue['status'] })}>
                  <option value="Open">Open</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldOwner}</label>
                {stakeholderNames.length === 0 ? (
                  <div style={{ ...styles.input, color: '#9ca3af', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                    {L.fieldOwnerNoStakeholders}
                  </div>
                ) : (
                  <select style={styles.select} value={form.owner || ''} onChange={e => setForm({ ...form, owner: e.target.value })}>
                    <option value="">{L.fieldOwnerPlaceholder}</option>
                    {stakeholderNames.map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                )}
              </div>
            </div>
            <div style={styles.twoCol}>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldDateIdentified}</label>
                <input style={styles.input} type="date" value={form.date_identified || ''} onChange={e => setForm({ ...form, date_identified: e.target.value })} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>{L.fieldDeadline}</label>
                <input style={styles.input} type="date" value={form.deadline || ''} onChange={e => setForm({ ...form, deadline: e.target.value })} />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldImpact}</label>
              <div style={styles.impactOptions}>
                {impactOptions.map(imp => (
                  <button key={imp} style={{ ...styles.impactBtn, backgroundColor: (form.impact || []).includes(imp) ? '#4f46e5' : '#f3f4f6', color: (form.impact || []).includes(imp) ? 'white' : '#374151' }} onClick={() => toggleImpact(imp)}>
                    {imp}
                  </button>
                ))}
              </div>
            </div>
            <div style={styles.field}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <label style={styles.label}>{L.fieldActionPlan}</label>
                <button
                  type="button"
                  disabled={aiPlanLoading || !form.title}
                  onClick={async () => {
                    setAiPlanLoading(true);
                    try {
                      const res = await axios.post(`${API}/advisor/${project.id}/suggest-mitigation`, {
                        type: 'issue', title: form.title, description: form.description,
                        extra: { priority: form.priority, category: form.category },
                      }, { headers: { Authorization: `Bearer ${token}` } });
                      setForm(f => ({ ...f, action_plan: res.data.suggestion }));
                    } catch { } finally { setAiPlanLoading(false); }
                  }}
                  style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: '1px solid #6366f1', background: aiPlanLoading ? '#e0e7ff' : '#eef2ff', color: '#4f46e5', cursor: form.title ? 'pointer' : 'not-allowed', fontWeight: 600 }}
                >
                  {aiPlanLoading ? '...' : L.fieldActionPlanAI}
                </button>
              </div>
              <textarea style={{ ...styles.input, height: '70px', resize: 'none' }} value={form.action_plan || ''} onChange={e => setForm({ ...form, action_plan: e.target.value })} placeholder={L.fieldActionPlanPlaceholder} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>{L.fieldRiskRef}</label>
              <input style={styles.input} value={form.risk_ref || ''} onChange={e => setForm({ ...form, risk_ref: e.target.value })} placeholder={L.fieldRiskRefPlaceholder} />
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={resetForm}>{C.cancel}</button>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky' as const, top: 64, zIndex: 99 },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px 48px', width: '100%', boxSizing: 'border-box' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  pageTitle: { fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  titleButtons: { display: 'flex', gap: '12px' },
  exportBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  addBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  tabWrapper: { display: 'flex', justifyContent: 'center', marginBottom: '24px' },
  tabContainer: { display: 'inline-flex', backgroundColor: 'white', borderRadius: '999px', padding: '4px', border: '1px solid #e5e7eb', gap: '2px' },
  tab: { padding: '10px 28px', borderRadius: '999px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s' },
  tabActive: { backgroundColor: '#1a1a2e', color: 'white' },
  tabInactive: { backgroundColor: 'transparent', color: '#6b7280' },
  loading: { color: '#666' },
  summaryRow: { display: 'flex', gap: '16px', marginBottom: '24px' },
  summaryCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', textAlign: 'center', flex: 1, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  summaryValue: { fontSize: '32px', fontWeight: '700', color: '#1a1a2e' },
  summaryLabel: { fontSize: '13px', color: '#9ca3af', marginTop: '4px' },
  dashboardGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  section: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  sectionTitle: { fontSize: '15px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 16px 0' },
  noIssues: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '32px', color: '#9ca3af', fontSize: '14px' },
  priorityCard: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px 14px', border: '1px solid #e5e7eb' },
  priorityCardHeader: { display: 'flex', gap: '8px', marginBottom: '8px' },
  priorityCardTitle: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', marginBottom: '6px' },
  priorityCardMeta: { display: 'flex', gap: '12px', fontSize: '12px', color: '#6b7280', flexWrap: 'wrap' },
  actionPlanPreview: { fontSize: '12px', color: '#6b7280', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb' },
  categoryRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  categoryLabel: { display: 'flex', alignItems: 'center', gap: '8px', width: '100px', flexShrink: 0 },
  categoryDot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0 },
  categoryName: { fontSize: '13px', color: '#374151' },
  categoryBarBg: { flex: 1, height: '8px', backgroundColor: '#f3f4f6', borderRadius: '999px', overflow: 'hidden' },
  categoryBar: { height: '100%', borderRadius: '999px', transition: 'width 0.3s' },
  categoryCount: { fontSize: '13px', fontWeight: '600', color: '#374151', width: '24px', textAlign: 'right' },
  statusGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  statusCard: { borderRadius: '8px', padding: '12px', textAlign: 'center' },
  statusCount: { fontSize: '24px', fontWeight: '700' },
  statusLabel: { fontSize: '12px', fontWeight: '500', marginTop: '2px' },
  emptyState: { textAlign: 'center', padding: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', color: '#9ca3af' },
  emptySubtext: { fontSize: '13px', maxWidth: '400px', lineHeight: '1.5' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f9fafb' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  tableRow: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '12px 16px', fontSize: '14px', color: '#374151', verticalAlign: 'top' },
  issueTitle: { fontWeight: '500', marginBottom: '2px' },
  issueDesc: { fontSize: '12px', color: '#9ca3af' },
  categoryChip: { padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: '500' },
  priorityBadge: { padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '600' },
  statusBadge: { padding: '3px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: '500' },
  statusSelect: { fontSize: '11px', padding: '4px 8px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '500' },
  impactPill: { backgroundColor: '#e0e7ff', color: '#3730a3', padding: '2px 6px', borderRadius: '999px', fontSize: '10px', fontWeight: '500' },
  editBtn: { backgroundColor: '#ede9fe', color: '#4f46e5', border: 'none', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' },
  deleteBtn: { backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', marginBottom: '24px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '6px', fontWeight: '500', color: '#333', fontSize: '14px' },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  select: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', outline: 'none', backgroundColor: 'white' },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  impactOptions: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  impactBtn: { padding: '6px 14px', borderRadius: '999px', border: 'none', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default Issues;
